'use client'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Tooltip } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { ICON_SIZES } from '../constants'
// ----------------------------------------------------------------------

interface ErrorTooltipIconProps {
  errorMessage: string
  showTooltip: boolean
  hasPasswordIcon?: boolean
  isSubmitted?: boolean
}

export interface ErrorTooltipIconRef {
  hideTooltip: () => void
}

export const ErrorTooltipIcon = forwardRef<ErrorTooltipIconRef, ErrorTooltipIconProps>(
  ({ errorMessage, showTooltip, hasPasswordIcon = false, isSubmitted = false }, ref) => {
    const [tooltipOpen, setTooltipOpen] = useState(false)

    // Expose method để ẩn tooltip từ parent component
    useImperativeHandle(ref, () => ({
      hideTooltip: () => setTooltipOpen(false),
    }))

    // Hiển thị tooltip khi submit và có lỗi
    useEffect(() => {
      if (isSubmitted && errorMessage) {
        setTooltipOpen(true)
      }
    }, [isSubmitted, errorMessage])

    const iconElement = (
      <ErrorOutlineIcon
        sx={{
          color: 'error.main',
          fontSize: `${ICON_SIZES.ERROR_ICON}px`,
          mr: hasPasswordIcon ? 0.5 : 0,
          cursor: 'pointer',
        }}
      />
    )

    // Desktop: Show MUI tooltip với controlled behavior
    if (showTooltip) {
      return (
        <Tooltip
          title={errorMessage}
          placement="top-end"
          arrow
          open={tooltipOpen}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#252422',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 300,
                lineHeight: 1.5,
                padding: '8px 12px',
                borderRadius: '8px',
                maxWidth: '300px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            },
            arrow: {
              sx: {
                color: '#252422',
                right: 16,
                top: '95%',
              },
            },
          }}
        >
          {iconElement}
        </Tooltip>
      )
    }

    // Mobile: Just show icon without tooltip
    return iconElement
  }
)

ErrorTooltipIcon.displayName = 'ErrorTooltipIcon'
